import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getAllMuestras } from "../../api/Muestras/muestraApi";
import { getProducts } from "../../api/Design/productApi";

function muestraLabel(m) {
  if (!m) return "";
  const code = String(m.MUESTRA || m.muestra || "").trim();
  const id = m.ID_MUESTRA ?? m.id;
  const product = String(
    m.producto?.nombre ||
      m.Producto?.nombre ||
      m.productName ||
      m.NOMBRE_PRODUCTO ||
      "",
  ).trim();
  if (code && product) return `${code} · ${product}`;
  if (code) return code;
  return id != null ? `#${id}` : "";
}

function productLabel(p) {
  if (!p) return "";
  const name = String(p.nombre || p.Nombre || "").trim();
  const id = p.id ?? p.ID_PRODUCTO;
  return name || (id != null ? `#${id}` : "");
}

/**
 * Adjuntar media de reunión a:
 * - muestra existente (chip + copiar archivos), o
 * - diseño existente (abrir creación de muestra sobre ese producto).
 */
export default function MeetingLinkMuestraDialog({
  open,
  onClose,
  onConfirmMuestra,
  onConfirmProduct,
  mediaCount = 0,
  linking = false,
  fullScreen = false,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [muestras, setMuestras] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedMuestra, setSelectedMuestra] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [muestraInput, setMuestraInput] = useState("");
  const [productInput, setProductInput] = useState("");
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!open) {
      setTab(0);
      setSelectedMuestra(null);
      setSelectedProduct(null);
      setMuestraInput("");
      setProductInput("");
      setLoadError("");
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([getAllMuestras(), getProducts()])
      .then(([mData, pData]) => {
        if (cancelled) return;
        setMuestras(Array.isArray(mData) ? mData : []);
        setProducts(Array.isArray(pData) ? pData : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.message || t("Meetings.linkMuestraLoadError"));
        setMuestras([]);
        setProducts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  const filteredMuestras = useMemo(() => {
    const q = String(muestraInput || "")
      .trim()
      .toLowerCase();
    const list = muestras || [];
    if (!q) return list.slice(0, 40);
    return list
      .filter((m) => {
        const hay = `${muestraLabel(m)} ${m.ID_MUESTRA || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 60);
  }, [muestras, muestraInput]);

  const filteredProducts = useMemo(() => {
    const q = String(productInput || "")
      .trim()
      .toLowerCase();
    const list = products || [];
    if (!q) return list.slice(0, 40);
    return list
      .filter((p) => {
        const hay = `${productLabel(p)} ${p.id || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 60);
  }, [products, productInput]);

  const canConfirmMuestra =
    tab === 0 &&
    !linking &&
    !loading &&
    selectedMuestra &&
    Number(selectedMuestra.ID_MUESTRA || selectedMuestra.id) > 0;

  const canConfirmProduct =
    tab === 1 &&
    !linking &&
    !loading &&
    selectedProduct &&
    Number(selectedProduct.id || selectedProduct.ID_PRODUCTO) > 0;

  const handleConfirm = () => {
    if (tab === 0) onConfirmMuestra?.(selectedMuestra);
    else onConfirmProduct?.(selectedProduct);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !linking && onClose?.()}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
    >
      <DialogTitle>{t("Meetings.linkExistingTitle")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1.5 }}>
          {t("Meetings.linkExistingBody", { count: mediaCount })}
        </DialogContentText>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, minHeight: 40 }}
        >
          <Tab label={t("Meetings.linkTabMuestra")} disabled={linking} />
          <Tab label={t("Meetings.linkTabProduct")} disabled={linking} />
        </Tabs>
        {loadError ? (
          <DialogContentText color="error" sx={{ mb: 1.5 }}>
            {loadError}
          </DialogContentText>
        ) : null}
        {tab === 0 ? (
          <>
            <DialogContentText sx={{ mb: 1.5 }} variant="body2">
              {t("Meetings.linkMuestraBody", { count: mediaCount })}
            </DialogContentText>
            <Autocomplete
              options={filteredMuestras}
              loading={loading}
              value={selectedMuestra}
              onChange={(_, value) => setSelectedMuestra(value)}
              inputValue={muestraInput}
              onInputChange={(_, value) => setMuestraInput(value)}
              getOptionLabel={(opt) => muestraLabel(opt)}
              isOptionEqualToValue={(a, b) =>
                Number(a?.ID_MUESTRA ?? a?.id) ===
                Number(b?.ID_MUESTRA ?? b?.id)
              }
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label={t("Meetings.linkMuestraSearch")}
                  placeholder={t("Meetings.linkMuestraPlaceholder")}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </>
        ) : (
          <>
            <DialogContentText sx={{ mb: 1.5 }} variant="body2">
              {t("Meetings.linkProductBody", { count: mediaCount })}
            </DialogContentText>
            <Autocomplete
              options={filteredProducts}
              loading={loading}
              value={selectedProduct}
              onChange={(_, value) => setSelectedProduct(value)}
              inputValue={productInput}
              onInputChange={(_, value) => setProductInput(value)}
              getOptionLabel={(opt) => productLabel(opt)}
              isOptionEqualToValue={(a, b) =>
                Number(a?.id ?? a?.ID_PRODUCTO) ===
                Number(b?.id ?? b?.ID_PRODUCTO)
              }
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label={t("Meetings.linkProductSearch")}
                  placeholder={t("Meetings.linkProductPlaceholder")}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={linking}>
          {t("Meetings.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={!(canConfirmMuestra || canConfirmProduct)}
          onClick={handleConfirm}
          startIcon={
            linking ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {tab === 0
            ? t("Meetings.linkMuestraConfirm")
            : t("Meetings.linkProductConfirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
